// Components
import { Stack, Button, Group, Alert, Textarea } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";

// Hooks
import { useForm } from "@mantine/form";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useCallback } from "react";

// Types
import type { CarePlan } from "@medplum/fhirtypes";

/**
 * Props for the care plan override form.
 * @agentic connections
 */
export type CarePlanOverrideFormProps = {
  carePlan: CarePlan;
  action: "complete" | "cancel";
  warning?: string;
  /**
   * Performs the FHIR write. Must NOT close the modal — closing is the
   * caller's responsibility via `onSuccess` so the form can call
   * `markAsClean()` first and bypass the navigation blocker.
   */
  onConfirm: (reason: string) => Promise<void>;
  /**
   * Called after a successful submit and after the form has marked
   * itself clean. Parents should use this to close the modal so that
   * the route-aware close (which is a `navigate()` call under the
   * hood) is no longer intercepted by the unsaved-changes blocker.
   */
  onSuccess?: () => void;
  onCancel?: () => void;
};

/**
 * Returns label for complete/cancel action.
 * @agentic connections
 */
const getActionLabel = (action: "complete" | "cancel"): string => {
  switch (action) {
    case "complete":
      return "Completion";
    case "cancel":
      return "Cancellation";
    default:
      return "Perform Action";
  }
};

/**
 * Form for capturing clinical justification when completing or canceling care plans.
 * @agentic connections
 */
export const CarePlanOverrideForm = ({
  carePlan: _carePlan,
  action,
  warning,
  onConfirm,
  onSuccess,
  onCancel,
}: CarePlanOverrideFormProps) => {
  const form = useForm({
    initialValues: {
      reason: "",
    },
    validate: {
      reason: (value) =>
        value.trim().length < 10
          ? "Please provide a reason for this action."
          : null,
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
    confirmationMessage:
      "You have unsaved changes. Are you sure you want to close?",
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  // Order matters: markAsClean() flips submitCleanRef synchronously so
  // the navigation blocker bypasses the modal-close navigate() that
  // onSuccess triggers. If the FHIR write throws, we never reach
  // markAsClean — the form stays dirty and the blocker keeps
  // protecting the user's input. Errors are logged here so they don't
  // surface as unhandled rejections; consumers are expected to surface
  // a user-visible notification from their own onConfirm handler.
  const handleSubmit = form.onSubmit(async (values) => {
    const handler = createSubmissionHandler(async () => {
      await onConfirm(values.reason);
      markAsClean();
      form.reset();
      onSuccess?.();
    });
    try {
      await handler(undefined);
    } catch (error) {
      console.error("CarePlanOverrideForm submission failed:", error);
    }
  });

  return (
    <>
      <form onSubmit={handleSubmit}>
        <FormSection title="" showDivider={false}>
          <Stack gap="md">
            {warning && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                title="Please Note:"
                color="gray"
                variant="light"
              >
                {warning}
              </Alert>
            )}

            <FormField label="Clinical Reason" required>
              <Textarea
                placeholder="Enter a reason for manually overriding this care plan..."
                required
                minRows={4}
                {...form.getInputProps("reason")}
              />
            </FormField>

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={handleCancel} type="button">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="light"
                loading={isSubmitting}
                color={action === "cancel" ? "red" : "green"}
              >
                Confirm {getActionLabel(action)}
              </Button>
            </Group>
          </Stack>
        </FormSection>
      </form>
      <ConfirmationModal />
    </>
  );
};
